import React, { useContext, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { message } from 'antd';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { useDocumentContext } from '../../../context/complexDocumentContext';
import ResizableColumn from '../DynamicTableDesign/ResizableColumn';
import CellOptions from '../CreativeBlockDesign/CellOptions';
import { DYNAMIC_TABLE_OPERATIONS } from '../DynamicTableDesign/translationValues';
import { COMPLEX_DOCUMENT_PAGE_WRAPPER } from '../../../utils/complexDocumentConstants';
import DynamicTableDragLayer from '../DynamicTableDesign/DynamicTableDragLayer';
import { ComplexEditingContext } from '../../../context/complexEditingContext';

const GridFormatting = (props) => {

  const { t, itemUUID, parentUUID, selectedRange, setSelectedRange, setSelectedCell, setContextMenu, tableRef, copiedCellsRef, scaling, moveableRef } = props;
  const { updateComplexElementDetails, copyGridElement, pasteGridElements, deleteCellContnet } = useContext(PageElementsContext);

  const customSelector = useCallback(store => store[itemUUID], []);
  const [gridBlock, methods] = useDocumentContext(customSelector, `GridDesign-${itemUUID}`);

  const pageDetailsSelector = useCallback((store) => store[parentUUID], []);
  const [pageDetails] = useDocumentContext(pageDetailsSelector, `GridDesign-${itemUUID}`);

  const imageRef = useContext(ComplexEditingContext);

  const onAddColumn = useCallback((e) => {
    const isLeft = e.currentTarget.id.includes('first');
    let columnIndex = 0;
    if (!isLeft) {
      columnIndex = parseInt(e.currentTarget.id.replaceAll(`add-col-${itemUUID}-`, ''), 10);
    }
    updateComplexElementDetails(
      itemUUID,
      {
        index: columnIndex,
        isLeft
      },
      'static-block-add-column'
    );
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
  }, []);

  const onColumnResize = useCallback((uuid, columnIndex, differenceInWidth) => {
    let diff = differenceInWidth;
    let index = columnIndex;
    let translate = false;
    if (columnIndex === 'first') {
      diff = differenceInWidth * -1;
      index = 0;
      translate = true;
    }
    updateComplexElementDetails(
      itemUUID,
      {
        index,
        diff,
        pageDetails,
        prompt: () => {
          message.warn(t(`DYNAMIC_TABLE_OPERATIONS:${DYNAMIC_TABLE_OPERATIONS.RESIZE_COLUMN_WARN.key}`, DYNAMIC_TABLE_OPERATIONS.RESIZE_COLUMN_WARN.value));
        },
        prevent: () => {
          message.warn(t(
            `DYNAMIC_TABLE_OPERATIONS:${DYNAMIC_TABLE_OPERATIONS.SOME_COLUMNS_ARE_TOO_SMALL_ON_RESIZE.key}`,
            DYNAMIC_TABLE_OPERATIONS.SOME_COLUMNS_ARE_TOO_SMALL_ON_RESIZE.value
          ));
        }
      },
      'static-block-resize-column'
    );
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
    if (moveableRef.current) moveableRef.current.updateRect();
  }, [pageDetails, gridBlock, moveableRef]);

  let cellRight = 0;
  
  const isPasteInProgress = useRef(false);
  const handleKeyboard = useCallback(
    async e => {
      const targetParent = e.target.parentElement;
      // return if quill editor is the target element or target element's parent is quill tooltip input box i.e, link input
      if (
        e.target.classList.contains('ql-editor') ||
        (targetParent && targetParent.classList.contains('ql-tooltip'))
      )
        return;
      // return if target is not inside 'complex-page-wrapper'
      const el = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
      if (!el.contains(e.target)) return;

      if (e && (e.ctrlKey || e.metaKey)) {
        switch (e.keyCode) {
          case 67:
            //ctl + c
            const copiedUUIDs = copyGridElement({ ...selectedRange });
            copiedCellsRef.current = copiedUUIDs;
            break;

          case 86:
            //ctl + v
            if (!isPasteInProgress.current && (copiedCellsRef.current || []).length > 0) {
              isPasteInProgress.current = true;
              pasteGridElements(
                selectedRange,
                copiedCellsRef.current,
                methods,
                itemUUID,
                parentUUID,
                () => {
                  setTimeout(() => {
                    copiedCellsRef.current = null;
                    setSelectedRange(undefined);
                    isPasteInProgress.current = false;
                  }, 100);
                },
              );
            }
            break;

          default:
            break;
        }
      }
      if (e && (e.keyCode === 8 || e.keyCode === 46)) {
        // backspace / delete
        const selectedRangeUUIDs = copyGridElement({ ...selectedRange });
        selectedRangeUUIDs.forEach(uuid => {
          if (imageRef.current.has(uuid)) {
            imageRef.current.delete(uuid);
          }
        });
        deleteCellContnet({ ...selectedRange }, itemUUID, parentUUID, methods);
      }
    },
    [selectedRange],
  );

  useEffect(() => {
    const handleTableStyling = e => {
      const { action, newElement } = e.detail;
      updateComplexElementDetails(
        itemUUID,
        {
          element: newElement,
        },
        'replace-table-element-with-styles'
      );
    }
    tableRef.current.addEventListener('updatingContentsForStyle', handleTableStyling);
    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('updatingContentsForStyle', handleTableStyling);
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboard);
    return () => {
      document.removeEventListener('keydown', handleKeyboard);
    };
  }, [handleKeyboard]);

  return (
    <>
        {gridBlock.style.tableProperties.columnWidths.map((colWidth, columnIndex) => {
          if (columnIndex === gridBlock.style.tableProperties.columnWidths.length - 1) return <div />;
          cellRight += colWidth;
          return (
            <ResizableColumn
              itemUUID={itemUUID}
              uuid={`col-${itemUUID}-${columnIndex}`}
              key={`col-${itemUUID}-${columnIndex}`}
              columnIndex={columnIndex}
              onResize={onColumnResize}
              left={0}
              leftStart={cellRight}
              scaling={scaling}
              isStatic={false}
              customClassName={'col-resizer'}
              onAddColumn={onAddColumn}
            />
        )})}
        <DynamicTableDragLayer scaling={scaling} tableRef={tableRef} tableUUID={itemUUID} />
        {selectedRange?.start && ReactDOM.createPortal(
          (
            <CellOptions
              selectedRange={selectedRange}
              element={gridBlock}
              updateComplexElementDetails={updateComplexElementDetails}
              tableRef={tableRef}
              flowUUID={pageDetails.uuid}
              staticTableBlock={true}
            />
          ),
          document.getElementById('document-editor-toolbar'),
        )}
    </>
  );
}

export default GridFormatting;